import React, { useCallback } from 'react';
import { Row, Text } from '@ydistri/ds';
import { rtkQueryPatchStatusToConfigurationFieldStatus } from '@ydistri/utils';
import { ConfigurationRuleSetupType } from '@ydistri/api-sdk';
import { useSelector } from 'react-redux';
import { setModalChanged, updateScopeConfiguration } from './scopeConfigurationSlice';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useMainScope } from './useMainScope';
import { useScope } from './useScope';
import { ReduxState, useAppDispatch } from '../../../store';
import { isNonModalViewMode, ViewMode } from './scopeUtilsLib';
import { Checkbox, Popover } from 'antd';
import { ImCross } from 'react-icons/im';
import { AiOutlineCheck } from 'react-icons/ai';
import { transformToConfigurationRule, usePutConfigurationRuleMutation } from './apiScopes';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';
import ScopeConfigurationField from './ScopeConfigurationField';
import ScopeConfigurationFieldPopover from './ScopeConfigurationFieldPopover';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { computeRemSize } from '@ydistri/theme';
import { ScopeConfigurationIconReplacement } from './scopeLib';
import { useStoresReport } from './useStoresReport';
import { overriddenFields } from './storesReportLib';

const iconLeftMargin = { marginLeft: '0.8rem' };

interface ScopeConfigurationFieldRowProps {
  scopeId: number;
  viewMode: ViewMode;
  field: ConfigurationRuleSetupType;
  disabled?: boolean;
  title: string;
  iconReplacement?: ScopeConfigurationIconReplacement;
  iconReplacementTooltip?: string;
}

const ScopeConfigurationFieldRow: React.FC<ScopeConfigurationFieldRowProps> = ({
  scopeId,
  viewMode,
  field,
  disabled,
  title,
  iconReplacement,
  iconReplacementTooltip,
}) => {
  const dispatch = useAppDispatch();
  const playgroundScope = useMainScope();
  const scopeById = useScope(scopeId);
  const templateOrCalculation = useTemplateOrCalculation();
  const isConfigurationEditable = useIsConfigurationEditable();

  const [putConfigurationRuleMutation, putConfigurationStatus] = usePutConfigurationRuleMutation();
  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const { problems } = useStoresReport();
  const fieldIsOverwritten = (overriddenFields(problems, scopeId)[field]?.length ?? 0) > 0;

  const status = rtkQueryPatchStatusToConfigurationFieldStatus(putConfigurationStatus);

  const isNewExceptionModal =
    viewMode === ViewMode.NEW_EXCEPTION_MODAL && scopeId === modalScope?.id;

  const scope = isNewExceptionModal ? modalScope : scopeById;

  const playgroundValue = playgroundScope?.configuration[field];
  const value = iconReplacement ? null : scope?.configuration[field];

  const onCheckboxChange = useCallback(
    (e: CheckboxChangeEvent) => {
      // finalValue will be used in case checkbox is checked
      const finalValue = playgroundValue;

      if (viewMode === ViewMode.EXCEPTION && scopeById) {
        const configurationRule = transformToConfigurationRule({
          ...scopeById,
          configuration: {
            ...scopeById.configuration,
            [field]: e.target.checked ? finalValue : null,
          },
        });

        putConfigurationRuleMutation({
          id: scopeId,
          templateId: templateOrCalculation.id,
          data: configurationRule,
        });
      } else {
        dispatch(
          updateScopeConfiguration({
            configuration: { [field]: e.target.checked ? finalValue : null },
          }),
        );
        dispatch(setModalChanged(true));
      }
    },
    [
      dispatch,
      field,
      playgroundValue,
      putConfigurationRuleMutation,
      scopeById,
      scopeId,
      templateOrCalculation.id,
      viewMode,
    ],
  );

  const onChangeSwitchHandler = useCallback(() => {
    if (isNonModalViewMode(viewMode) && scopeById) {
      const configurationObject = {
        ...scopeById.configuration,
        [field]: !value,
      };

      const configurationRule = transformToConfigurationRule({
        ...scopeById,
        configuration: configurationObject,
      });

      putConfigurationRuleMutation({
        id: scopeId,
        templateId: templateOrCalculation.id,
        data: configurationRule,
      });
    } else {
      dispatch(updateScopeConfiguration({ configuration: { [field]: !value } }));
    }
  }, [
    dispatch,
    field,
    putConfigurationRuleMutation,
    scopeById,
    scopeId,
    templateOrCalculation.id,
    value,
    viewMode,
  ]);

  const onChangeInputHandler = useCallback(
    (newValue: string) => {
      if (isNonModalViewMode(viewMode) && scopeById) {
        if (newValue === '') {
          newValue = '0';
        }
        const configurationRule = transformToConfigurationRule({
          ...scopeById,
          configuration: {
            ...scopeById.configuration,
            [field]: parseInt(newValue),
          },
        });

        putConfigurationRuleMutation({
          id: scopeId,
          templateId: templateOrCalculation.id,
          data: configurationRule,
        });
      } else {
        dispatch(updateScopeConfiguration({ configuration: { [field]: parseInt(newValue) } }));
      }
    },
    [
      dispatch,
      field,
      putConfigurationRuleMutation,
      scopeById,
      scopeId,
      templateOrCalculation.id,
      viewMode,
    ],
  );

  const disabledFinal = isConfigurationEditable ? disabled : true;

  return (
    <Row $minHeight="2rem" $gap="0.5rem" $alignItems="center" $flexWrap="wrap">
      <Row $minWidth={computeRemSize(20)}>
        {viewMode !== ViewMode.GENERAL_CONFIGURATION && (
          <Checkbox
            onChange={onCheckboxChange}
            checked={value !== null}
            disabled={iconReplacement !== undefined || !isConfigurationEditable}
          />
        )}
      </Row>
      <Row $minWidth={computeRemSize(220)} $alignItems="center">
        <Text $type={value !== null ? 'default' : 'light'} $size="large" $semiBold>
          {title}
        </Text>
      </Row>
      <Row $minWidth={computeRemSize(100)}>
        {iconReplacement ? (
          <Popover content={iconReplacementTooltip}>
            {iconReplacement === 'cross' && (
              <ImCross color="grey" size={computeRemSize(18)} style={iconLeftMargin} />
            )}
            {iconReplacement === 'check' && (
              <AiOutlineCheck color="green" size={computeRemSize(18)} style={iconLeftMargin} />
            )}
          </Popover>
        ) : (
          <ScopeConfigurationFieldPopover field={field} scopeId={scopeId}>
            <ScopeConfigurationField
              viewMode={viewMode}
              value={value}
              field={field}
              disabledFinal={disabledFinal}
              onChangeSwitchHandler={onChangeSwitchHandler}
              onChangeInputHandler={onChangeInputHandler}
              status={status}
              fieldIsOverwritten={fieldIsOverwritten}
            />
          </ScopeConfigurationFieldPopover>
        )}
      </Row>
    </Row>
  );
};

export default ScopeConfigurationFieldRow;
